import { Component, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { UserCodeTypeEnum, UserModel } from '@bp-core/src/lib/proto/common-message.pb';
import { GetActiveCodesRequest } from '@bp-core/src/lib/proto/user.pb';
import { UserGrpcServiceClient } from '@bp-core/src/lib/proto/user.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { WVCommunicationService } from '@bp-core/src/lib/services/wvcommunication.service';
import { environment } from '@environment/environment';
import { Patrimony } from 'bp/modules/dashboard/models/patrimony-summary';
import { Step } from 'bp/shared/models/first-step.enum';
import { UserStateService } from 'bp/shared/services/user-state.service';
import { SharedModule } from 'bp/shared/shared.module';
import { BETTERPLAN_US_LINK, showsBetterplanUs } from 'bp/shared/utils/betterplan-us-menu.utils';
import { Subject, catchError, combineLatest, of, takeUntil } from 'rxjs';

@Component({
  selector: 'bp-user',
  templateUrl: './bp-user.component.html',
  styles: [
    `:host ::ng-deep .mat-mdc-icon-button img { height: 1.75rem !important; width: 1.75rem !important; }`,
  ],
  exportAs: 'bp-user',
  imports: [
    MatTooltipModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatIconModule,
    SharedModule,
    RouterModule,
  ],
})
export class BpUserComponent implements OnInit {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  user: UserModel;
  avatarFirstName: string;
  avatarLastName: string;
  isDebugMode = false;
  isAdminUser = false;
  canChangeUser = false;
  canGotoMiPerfil = false;
  avatarImgUrl: string;
  urlBaseFile = environment.urlApiV2 + 'api/Files/';
  /** Patrimonio por entidad (resumen). Con UNA sola entidad, "Balance General"
   *  navega filtrado por ella; con varias, va al balance general sin filtro. */
  private entityPatrimonies: Patrimony[] = [];
  /** BetterplanUS, con la condición que tenía el menú lateral (FR-002). */
  readonly showBetterplanUs = signal(false);
  readonly betterplanUsLink = BETTERPLAN_US_LINK;

  constructor(
    private authService: AuthService,
    private userGrpcServiceClient: UserGrpcServiceClient,
    private wvCommunicationService: WVCommunicationService,
    private userStateService: UserStateService,
    private router: Router,
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.isDebugMode = localStorage.getItem('bp_debug') === 'True' ? true : false;

    // Get user
    this.authService.userSubject$.pipe(takeUntil(this._unsubscribeAll)).subscribe(user => {
      if (user) {
        this.user = user;
        //console.warn(user);

        // Set isAdminUser
        this.isAdminUser = this.user.isAdmin;

        // Set canChangeUser
        // `companies` (en el backend es [InverseProperty("Manager")]) trae las cuentas que el
        // usuario administra e incluye, en la mayoria de los casos, una fila del usuario
        // CONSIGO MISMO (companyUserId == su propio id) que el backend crea al asociar una
        // empresa. Esa fila no es una cuenta a la que "cambiarse", asi que se descuenta: basta
        // UNA cuenta distinta de la propia para que la opcion tenga sentido (el + esa cuenta).
        // Contarla haria aparecer la opcion a quien no administra ninguna otra cuenta, y con
        // el `> 1` original no la veia quien administraba exactamente una empresa.
        const managedAccounts =
          user.companies?.filter(company => company.companyUserId !== user.id) ?? [];

        if (managedAccounts.length >= 1 || (user.manager && user.id != user.manager.id)) {
          this.canChangeUser = true;
        }

        // Set avatarImgUrl
        if (user && user.photoProfileId?.value) {
          const token = localStorage.getItem('access_token');
          this.avatarImgUrl = this.urlBaseFile + user.photoProfileId?.value + '/?token=' + token;
        }

        // Set avatarFirstName && avatarLastName
        if (user?.firstname && user?.surname) {
          if (
            user?.firstname?.split(/\W+/).length >= 2 &&
            user?.surname?.split(/\W+/).length >= 2
          ) {
            this.avatarFirstName = user?.firstname?.replace(/ .*/, '');
            this.avatarLastName = user?.surname?.replace(/ .*/, '');
          } else if (
            user?.firstname.split(/\W+/).length >= 2 &&
            user?.surname.split(/\W+/).length < 2
          ) {
            this.avatarFirstName = user?.firstname.replace(/ .*/, '');
            this.avatarLastName = user?.surname;
          } else if (
            user?.firstname.split(/\W+/).length < 2 &&
            user?.surname.split(/\W+/).length >= 2
          ) {
            this.avatarFirstName = user?.firstname;
            this.avatarLastName = user?.surname.replace(/ .*/, '');
          } else {
            this.avatarFirstName = user?.firstname;
            this.avatarLastName = user?.surname;
          }
        }
      }
    });

    // set canGotoMiPerfil
    this.userStateService.currentStep$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(currentStepInfo => {
        if (
          currentStepInfo?.currentStep === Step.DONE ||
          currentStepInfo?.currentStep === Step.FOURTH
        ) {
          this.canGotoMiPerfil = true;
        }
      });

    // BetterplanUS: Vector activo + invitación BP_US con estado. Si la consulta falla, el menú
    // sigue sin la entrada.
    combineLatest([
      this.authService.userSubject$,
      this.userGrpcServiceClient
        .getActiveCodes(new GetActiveCodesRequest({ type: UserCodeTypeEnum.BP_US_INVITATION }))
        .pipe(catchError(() => of(undefined))),
    ])
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(([user, invitations]) =>
        this.showBetterplanUs.set(showsBetterplanUs(user, invitations)),
      );

    // Patrimonio por entidad (para decidir el destino de "Balance General").
    this.userStateService.patrimonySummary$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(patrimonySummary => {
        this.entityPatrimonies = patrimonySummary?.entityPatrimonies ?? [];
      });
  }

  /**
   * "Balance General" del menú:
   * - Con UNA sola entidad → navega filtrado por ella (`?entity=<uuid>`).
   * - Con varias (o sin data) → balance general sin filtro (comportamiento previo).
   */
  onClickGeneralBalance(): void {
    if (this.entityPatrimonies.length === 1) {
      this.router.navigate(['/general-balance'], {
        queryParams: { entity: this.entityPatrimonies[0].entity },
      });
      return;
    }
    this.router.navigate(['/general-balance']);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  showDebugPage(): void {
    this.wvCommunicationService.showDebugPage();
  }

  onLogout(): void {
    this.authService.logout();
  }
}
